import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { TreeSelect } from "antd";
import CriterionSummary from "./CriterionSummary";
import { getSyndicatedData } from "../../../services/cohort";
import "./SyndicatedCard.scss";

/**
 * SyndicatedCard - Criteria card for targeting third-party data segments
 *
 * Data format: {
 *   segments: [{ id: "...", title: "..." }]
 * }
 */
function SyndicatedCard({ data, onChange, isReadOnly, isCollapsed, tagId, dataCenter = "us-east-1", subdomain }) {
	const [syndicatedOptions, setSyndicatedOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	// Fetch syndicated data options on mount
	useEffect(() => {
		const fetchOptions = async () => {
			if (!tagId) return;

			try {
				setLoading(true);
				const response = await getSyndicatedData(tagId);

				// Filter providers by region
				const Experian = dataCenter === "eu-west-1" ? "Experian UK" : "Experian";
				const allowedProviders =
					dataCenter === "eu-west-1"
						? [Experian, "Bombora", "Genius Sports", "Retail Innovation Lab", "Experian Worldview"]
						: [Experian, "Bombora", "LiveRamp", "Genius Sports", "Retail Innovation Lab", "Experian Worldview",
						   ...(subdomain === "silo60" ? ["Experian UK"] : [])];

				// Transform to TreeSelect format
				const groups = response
					.filter(({ provider }) => allowedProviders.includes(provider))
					.map(({ provider, segments }) => ({
						value: provider,
						title: provider,
						checkable: false,
						children: segments.map(({ category, segments: subSegments }) => ({
							value: `${provider}|${category}`,
							title: category,
							checkable: false,
							children: subSegments.map(({ id, title }) => ({
								value: `${id}|${title}`,
								title,
							})),
						})),
					}));

				setSyndicatedOptions(groups);
			} catch (error) {
				console.error("Failed to fetch syndicated data options:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchOptions();
	}, [tagId, dataCenter]);

	// Get selected items for TreeSelect with labels (using labelInValue)
	const getSelectedValues = () => {
		if (!data?.segments) return [];
		return data.segments.map((s) => ({
			value: `${s.id}|${s.title || s.id}`,
			label: s.title || s.id,
		}));
	};

	// Build a map from segment ID to provider name using the fetched tree
	const getSegmentProviderMap = () => {
		const map = {};
		for (const provider of syndicatedOptions) {
			for (const category of provider.children || []) {
				for (const segment of category.children || []) {
					const [id] = segment.value.split("|");
					map[id] = provider.title;
				}
			}
		}
		return map;
	};

	// Handle selection change
	const handleSelectionChange = (selectedValues) => {
		const segments = (selectedValues || []).map((item) => {
			const value = typeof item === "object" ? item.value : item;
			const [id, ...titleParts] = value.split("|");
			return { id, title: titleParts.join("|") };
		});

		onChange({
			...data,
			segments,
		});
	};

	// Get provider names for label
	const getProviderNames = () => {
		return syndicatedOptions.map((opt) => opt.title).join(", ");
	};

	// Collapsed view - show selected items as chips grouped by provider
	if (isCollapsed) {
		return (
			<div className="syndicated-card collapsed">
				<CriterionSummary
					type="syndicated"
					data={data}
					syndicatedOptions={syndicatedOptions}
				/>
			</div>
		);
	}

	// Expanded view
	return (
		<div className="syndicated-card expanded">
			{/* Info text */}
			<div className="input-wrapper">
				<label className="chips-label">
					Target Segments From {loading ? "..." : getProviderNames()}
				</label>
				<div className="input-info">Add Segments</div>
			</div>

			{/* TreeSelect for segment selection */}
			<div className="tree-select syndicated">
				<TreeSelect
					showSearch
					style={{ width: "100%" }}
					value={getSelectedValues()}
					dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
					placeholder={loading ? "Loading segments..." : "Select segments..."}
					treeCheckable
					allowClear
					variant="borderless"
					showCheckedStrategy={TreeSelect.SHOW_ALL}
					onChange={handleSelectionChange}
					treeData={syndicatedOptions}
					autoClearSearchValue={false}
					disabled={isReadOnly || loading}
					labelInValue
				/>
			</div>
		</div>
	);
}

SyndicatedCard.propTypes = {
	data: PropTypes.shape({
		segments: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.string,
				title: PropTypes.string,
			})
		),
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	isCollapsed: PropTypes.bool,
	tagId: PropTypes.string,
	dataCenter: PropTypes.string,
	subdomain: PropTypes.string,
};

SyndicatedCard.defaultProps = {
	data: { segments: [] },
	isReadOnly: false,
	isCollapsed: false,
	tagId: null,
	dataCenter: "us-east-1",
	subdomain: null,
};

// Export helper for getting selected count (used in header)
SyndicatedCard.getSelectedCount = (data) => {
	return data?.segments?.length || 0;
};

export default SyndicatedCard;
